import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Html } from '@react-three/drei';
import axios from 'axios';
import { API } from '../../config/api';

const http = axios.create({ baseURL: API.BACKEND, timeout: 10000 });
http.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) config.headers['x-auth-token'] = token;
  return config;
});

const COLOR_HEX = {
  BLUE: '#3B82F6', BROWN: '#8B4513', 'GREEN-YELLOW': '#9ACD32',
  GREY: '#808080', BLACK: '#333', WHITE: '#EEE', RED: '#EF4444',
  YELLOW: '#F59E0B', GREEN: '#22C55E', ORANGE: '#F97316'
};

function useYardItems() {
  return useQuery({
    queryKey: ['warehouse', 'yard-items'],
    queryFn: async () => {
      const res = await http.get('/api/v1/warehouse/items?location=yard');
      return res.data?.data || [];
    },
    refetchInterval: 15000,
    staleTime: 10000
  });
}

// 3D drum in the yard
//
// Clickable: a drum standing in the yard is finished cable waiting for the
// warehouse, and "finished" is exactly the decision somebody changes their mind
// about. Selecting it opens the same Add-phase dialog the Production → Warehouse
// table has, so the drum can be sent back to production from where you are
// looking at it.
function YardDrum({ position, item, onSelect }) {
  const r = 0.5;
  const cableColor = COLOR_HEX[item.traceability?.color] || '#444';
  const isDrum = item.type === 'drum';
  const clickable = typeof onSelect === 'function';

  return (
    <group
      position={position}
      onClick={
        clickable
          ? e => {
              // Without this the click carries on to the building behind the
              // drum and the camera flies off to focus it.
              e.stopPropagation();
              onSelect(item);
            }
          : undefined
      }
      onPointerOver={
        clickable
          ? e => {
              e.stopPropagation();
              document.body.style.cursor = 'pointer';
            }
          : undefined
      }
      onPointerOut={
        clickable
          ? () => {
              document.body.style.cursor = 'auto';
            }
          : undefined
      }
    >
      {isDrum ? (
        <>
          {/* Drum barrel */}
          <mesh position={[0, r + 0.02, 0]} rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[r * 0.4, r * 0.4, r * 1.1, 12]} />
            <meshStandardMaterial color="#5C4020" roughness={0.8} />
          </mesh>
          {/* Flanges */}
          <mesh position={[0, r + 0.02, r * 0.58]} rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[r, r, 0.025, 14]} />
            <meshStandardMaterial color="#8B6914" roughness={0.7} />
          </mesh>
          <mesh position={[0, r + 0.02, -r * 0.58]} rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[r, r, 0.025, 14]} />
            <meshStandardMaterial color="#8B6914" roughness={0.7} />
          </mesh>
          {/* Cable */}
          <mesh position={[0, r + 0.02, 0]} rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[r * 0.85, r * 0.5, r * 0.9, 12]} />
            <meshStandardMaterial color={cableColor} roughness={0.9} />
          </mesh>
        </>
      ) : (
        <>
          {/* Europallet base */}
          <mesh position={[0, 0.075, 0]}>
            <boxGeometry args={[1.0, 0.15, 0.7]} />
            <meshStandardMaterial color="#8b5a2b" roughness={0.9} />
          </mesh>
          {/* Cargo */}
          <mesh position={[0, 0.6, 0]}>
            <boxGeometry args={[0.9, 0.9, 0.65]} />
            <meshStandardMaterial color="#3a95c9" roughness={0.6} />
          </mesh>
        </>
      )}

      {/* Label */}
      <Html position={[0, isDrum ? 1.3 : 1.4, 0]} center>
        <div style={{
          background: 'rgba(10,15,25,0.92)', color: '#FFF', padding: '3px 6px',
          borderRadius: 3, fontSize: 8, fontFamily: 'monospace',
          border: '1px solid #F59E0B50', userSelect: 'none', pointerEvents: 'none',
          whiteSpace: 'nowrap'
        }}>
          <div style={{ fontWeight: 700, fontSize: 8, overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 90 }}>
            {item.cableName}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 3, marginTop: 1 }}>
            {item.traceability?.color && (
              <div style={{ width: 5, height: 5, borderRadius: 1, background: cableColor }} />
            )}
            <span>{item.lengthMeters}m</span>
            <span style={{ color: '#888' }}>{item.weightKg}kg</span>
          </div>
          <div style={{ fontSize: 7, marginTop: 1 }}>
            {item.customerName ? (
              <span style={{ color: '#7DB3FF' }}>{item.customerName}</span>
            ) : (
              <span style={{ color: '#888' }}>STOCK</span>
            )}
          </div>
          <div style={{ fontSize: 7, color: '#888', marginTop: 1 }}>
            {item.serial}
          </div>
          {clickable && (
            <div style={{ fontSize: 6, color: '#F59E0B', marginTop: 2, letterSpacing: 0.5 }}>
              CLICK TO EDIT
            </div>
          )}
        </div>
      </Html>
    </group>
  );
}

// Group header for yard items
function YardGroupHeader({ position, label, count }) {
  return (
    <Html position={position} center>
      <div style={{
        background: 'rgba(20,30,50,0.95)', color: '#FFF', padding: '4px 10px',
        borderRadius: 4, fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap',
        border: '1px solid #F59E0B60', userSelect: 'none', pointerEvents: 'none'
      }}>
        YARD — {label} <span style={{ opacity: 0.5 }}>({count})</span>
      </div>
    </Html>
  );
}

// ─── YARD VISUALIZATION ────────────────────────────────────
// Shows drums/pallets on the road between halls and deposits
// Road is at X≈39 (between right building X=35 and Deposit 1 X=43)

export function YardVisualization({ onSelectItem }) {
  const { data: items } = useYardItems();

  // Split items by source hall based on machineId
  const { hall1Items, hall3Items } = useMemo(() => {
    if (!items?.length) return { hall1Items: [], hall3Items: [] };
    const h1machines = ['l75', 'l120', 'dtb1250', 'skip2250'];
    const h1 = [];
    const h3 = [];
    for (const item of items) {
      const mid = (item.traceability?.machineId || '').toLowerCase();
      if (h1machines.includes(mid)) h1.push(item);
      else h3.push(item);
    }
    return { hall1Items: h1, hall3Items: h3 };
  }, [items]);

  // Yard: along each hall's ROAD side wall, starting near the entrance
  // Hall 1 road wall at X=-4, yard in the road at X≈-2
  // Hall 3 road wall at X=11, yard in the road at X≈9
  const DZ = 1.3;
  const DX = 1.3;
  const zStart = 2;
  const itemsPerRow = 50;

  return (
    <group>
      {/* Hall 1 yard — road side (X≈-2), entrance to back */}
      {hall1Items.length > 0 && (
        <>
          <YardGroupHeader
            position={[-2, 2, zStart - 1]}
            label="Hall 1"
            count={hall1Items.length}
          />
          {hall1Items.map((item, i) => {
            const row = i % itemsPerRow;
            const col = Math.floor(i / itemsPerRow);
            return (
              <YardDrum
                key={item.serial}
                position={[-2 + col * DX, 0, zStart + row * DZ]}
                item={item}
                onSelect={onSelectItem}
              />
            );
          })}
        </>
      )}

      {/* Hall 3/4 yard — road side (X≈9), entrance to back */}
      {hall3Items.length > 0 && (
        <>
          <YardGroupHeader
            position={[9, 2, zStart - 1]}
            label="Hall 3/4"
            count={hall3Items.length}
          />
          {hall3Items.map((item, i) => {
            const row = i % itemsPerRow;
            const col = Math.floor(i / itemsPerRow);
            return (
              <YardDrum
                key={item.serial}
                position={[9 - col * DX, 0, zStart + row * DZ]}
                item={item}
                onSelect={onSelectItem}
              />
            );
          })}
        </>
      )}
    </group>
  );
}
